import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FilterFunction, ReportEdit } from '../report-list/report-edit';
import { merge, reject } from 'lodash';
import { ReportService } from 'src/app/core/service/report.service';
import { SelectEvent } from '@progress/kendo-angular-layout';
import { GridEventHandler, GridSettings } from 'src/app/core/model/grid-settings';
import { User } from 'src/app/user/model/user';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { sortBy } from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { Code } from 'src/app/core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { ViewService } from 'src/app/core/service/view.service';

export interface ExtractSaveEvent
{
  data: ReportEdit;
  apply: boolean;
}

@Component({
  standalone: false,
  selector: 'app-report-edit',
  templateUrl: './report-edit.component.html',
  styleUrls: ['./report-edit.component.scss']
})
export class ReportEditComponent implements OnInit
{
  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;

  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<ExtractSaveEvent> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public reportEdit: ReportEdit;
  public sqlViews: Array<{ name: string; viewName: string }> = [];
  public selectedTab = 0;
  public previousViewName = null;
  public view: GridViewManager;
  public count = 0;
  public recordDisabledText = '';
  public recordDeletedText = '';
  public functionNameAlreadyExistsText = '';
  public functionContainsBlackListedCodeText = '';
  public staffList = [];
  public windowState = 'default';
  public isDataAnalyst = false;
  public extractMetrics: Code[];
  public extractReports: Code[];
  public extracts: Array<{ text: string; value: string }> = [];

  /**
   * Report staff.
   */
  public staff = [];

  /**
   * Filtered list of staff candidates for drop-down.
   */
  public staffCandidatesFiltered: User[] = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * The newly added staff member.
   */
  public newStaffMember = null;

  /**
   * Candidate users for report staff drop-down.
   */
  private staffCandidates = [];

  private gridSettings = null;
  private previousReportType: string = null;

  constructor(
    private messengerService: MessengerService,
    private fb: UntypedFormBuilder,
    private authService: AuthenticationService,
    private userService: UserService,
    private translate: TranslateService,
    private messageHandlerService: MessageHandlerService,
    private reportService: ReportService,
    private dictionaryService: DictionaryService,
    private viewService: ViewService,)
  {
  }

  /**
   * Getter for form control field users.
   */
  public get users(): UntypedFormControl
  {
    return this.editForm.get('users');
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get reportType(): UntypedFormControl
  {
    return this.editForm.get('reportType');
  }

  public get sqlViewName(): UntypedFormControl
  {
    return this.editForm.get('sqlViewName');
  }

  public get privateReport(): UntypedFormControl
  {
    return this.editForm.get('privateReport');
  }

  @Input() public set model(reportEdit: ReportEdit)
  {
    this.initModel(reportEdit);
  }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(99999);

    this.translate.get(['RECORD-DISABLED', 'RECORD-DELETED', 'FUNCTION-ALREADY-EXISTS', 'FUNCTION-BLACKLISTED-CODE']).subscribe({
      next: (res) =>
      {
        this.recordDisabledText = res['RECORD-DISABLED'];
        this.recordDeletedText = res['RECORD-DELETED'];
        this.functionNameAlreadyExistsText = res['FUNCTION-ALREADY-EXISTS'];
        this.functionContainsBlackListedCodeText = res['FUNCTION-BLACKLISTED-CODE'];
      }
    });

    this.isDataAnalyst = (this.authService.currentUser.role === 'ROLE_DATA_ANALYST');
    this.userService.getCachedUsers().subscribe({
      next: (data: any) =>
      {
        if (this.isDataAnalyst)
        {
          this.userService.getUserById(this.authService.currentUser.id, false).subscribe({
            next: (user: any) =>
            {
              this.staffList = data.filter(x => x.organization?.id === user?.organization.id);
            }
          });
        }
        else
        {
          this.staffList = data;
        }
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Save report
   *
   * @param e Event
   */
  public onSave(e: Event, isApply: boolean): void
  {
    e.preventDefault();

    let validationErrors = false;
    this.editForm.controls.filterFunctions.controls.forEach((x) =>
    {
      // Validate if new function names (null id) not already exists as part of the view
      if (x.controls.id.value === null)
      {
        const foundColumn = this.gridSettings.columnsConfig.find(c => c.field === x.controls.name.value);
        if (foundColumn !== undefined)
        {
          this.messageHandlerService.displayDirectMessage('error',
            this.functionNameAlreadyExistsText.replace('{{value}}', foundColumn.field));
          validationErrors = true;
        }
      }
      // Validate for blacklisted commands
      if (this.isBlackListedSQLKeywords(x.controls.functionCode.value))
      {
        this.messageHandlerService.displayDirectMessage('error',
          this.functionContainsBlackListedCodeText.replace('{{value}}', x.controls.name.value));
        validationErrors = true;
      }
    });

    if (validationErrors)
    {
      return;
    }

    const objectToPersist = merge(this.reportEdit, this.editForm.value);

    objectToPersist.sqlViewFilter.name = objectToPersist.name;
    // Reset page offset before save
    if (this.gridSettings.state && this.gridSettings.state.skip !== 0)
    {
      this.gridSettings.state.skip = 0;
    }
    objectToPersist.sqlViewFilter.state = JSON.stringify(this.gridSettings.state);
    objectToPersist.sqlViewFilter.columns = JSON.stringify(this.gridSettings.columnsConfig);
    objectToPersist.privateReport = this.privateReport.value;

    if (this.privateReport.value)
    {
      objectToPersist.users = this.staff;
    }
    else
    {
      objectToPersist.users = [];
    }

    // Clean up filter functions
    objectToPersist.sqlViewFilter.filterFunctions = this.reportEdit.filterFunctions;
    this.setFilterFunctions(objectToPersist);

    // Emit save event
    this.save.emit({ data: objectToPersist, apply: isApply });
    this.editFormActive = isApply;
    if (!isApply)
    {
      this.selectedTab = 0;
    }

    // Force refresh of the filter text
    this.messengerService.sendMessage(new MessageSubject('refreshReportFilterText', { apply: isApply }));
  }

  /**
   * Track selected tab. This controls the tab width and increase the Design tab width if selected.
   *
   * @param e Select tab event.
   */
  public onTabSelect = (e: SelectEvent): void =>
  {
    this.selectedTab = e.index;
  };

  /**
   * Grid setting have been changed (filters, column order, sorting, column sizes)
   *
   * @param event New grid settings values.
   */
  public gridSettingsChanged(event: GridSettings): void
  {
    this.gridSettings = event;
    this.editForm.markAsDirty();
  }

  /**
   * Report view have been changed.
   *
   * @param viewName Indicate the new view selected.
   */
  public viewChanged(viewName: string): void
  {
    if (viewName !== undefined)
    {
      // In case changing view name using the same columns, we don't want to reset gridSettings.
      // But if this is a different view, then it must be reset.
      const view1 = viewName !== undefined && viewName !== null ? viewName.replace('_MATERIALIZED', '') : '';
      const view2 = this.previousViewName !== undefined && this.previousViewName !== null ?
        this.previousViewName.replace('_MATERIALIZED', '') : '';
      if (view1 !== view2)
      {
        this.gridSettings = '';
        this.previousViewName = viewName;
      }
    }
  }

  /**
   * Filters the report staff based on user input.
   */
  public filterStaffCandidates(filterValue: string): void
  {
    this.searchTerm = filterValue;
    this.staffCandidatesFiltered = this.staffCandidates
      .filter(user => user.name.toLowerCase().includes(filterValue.toLowerCase()));
  }

  /**
   * Delete user from list.
   *
   *@param user User to delete.
   */
  public deleteStaff(user: User): void
  {
    this.staff = this.staff.filter(u => u.id !== user.id);
    this.view.state.skip = 0;
    this.view.setData(this.staff);
    this.editForm.markAsDirty();
    this.count = this.staff.length;
  }

  /**
   * Staff cancel handler
   */
  public staffCancelHandler(event: GridEventHandler): void
  {
    event.sender.closeRow(event.rowIndex);
  }

  /**
   * Staff add handler
   */
  public staffAddHandler(event: GridEventHandler): void
  {
    this.newStaffMember = null;
    this.staffCandidates = sortBy(this.staffList.filter(u => !this.staff.map(m => m.id).includes(u.id)), ['name']);
    this.filterStaffCandidates('');
    event.sender.addRow(new UntypedFormGroup({ editing: new UntypedFormControl(true) }));
  }

  /**
   * Add staff to grid in preparation for save.
   */
  public staffSaveHandler(event: GridEventHandler): void
  {
    event.sender.closeRow(event.rowIndex);
    this.staff.unshift(this.newStaffMember);
    this.staff = sortBy(this.staff, ['name']);
    this.view.state.skip = 0;
    this.view.setData(this.staff);
    this.count = this.staff.length;
    this.editForm.markAsDirty();
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled && !itemArgs.dataItem.inRegistration;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Edit form has been closed.
   */
  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  public setRecordStateText(dataItem: any): string
  {
    if (dataItem && dataItem.deleted)
    {
      return this.recordDeletedText;
    }
    else if (dataItem && !dataItem.enabled)
    {
      return this.recordDisabledText;
    }
    else
    {
      return '';
    }
  }

  public onWindowStateChange(state: string): void
  {
    this.windowState = state;
  }

  public addFunctionEvent(event: FilterFunction): void
  {
    this.addFunction(event.id, event.version, event.name, event.functionCode, event.dataType,
      event.columnNameVar1, event.columnNameVar2, event.columnNameVar3);
  }

  public removeFunctionEvent(event: any): void
  {
    this.removeFunction(event.i, event.id);
  }

  public showTooltip(e: ElementRef): void
  {
    this.tooltipDir.toggle(e, true);
  }
  
  public hideTooltip(): void
  {
    this.tooltipDir.hide();
  }

  private addFunction(id: number = null, version = 0, name: string = null, functionCode: string = null, dataType: string = null,
    columnNameVar1: string = null, columnNameVar2: string = null, columnNameVar3: string = null): void
  {
    this.editForm.controls.filterFunctions.push(this.initFunction(id, version, name, functionCode, dataType,
      columnNameVar1, columnNameVar2, columnNameVar3));
    this.editForm.markAsDirty();

    const controlIndex = this.editForm.controls.filterFunctions.controls.length - 1;
    this.editForm.controls.filterFunctions.controls[controlIndex].get('functionCode').valueChanges.subscribe({
      next: (data: string) =>
      {
        this.setVariableValidator(data, controlIndex, '1');
        this.setVariableValidator(data, controlIndex, '2');
        this.setVariableValidator(data, controlIndex, '3');
      }
    });
  }

  private setVariableValidator(functionCode: string, controlIndex: number, variableNumber: string): void
  {
    if (functionCode.toUpperCase().includes(`$VAR${variableNumber}`))
    {
      this.editForm.controls.filterFunctions.controls[controlIndex].get(`columnNameVar${variableNumber}`)
        .setValidators([Validators.required]);
    }
    else
    {
      this.editForm.controls.filterFunctions.controls[controlIndex].get(`columnNameVar${variableNumber}`).setValidators([]);
    }
    this.editForm.controls.filterFunctions.controls[controlIndex].get(`columnNameVar${variableNumber}`).updateValueAndValidity();
  }

  private removeFunction(i: number, id: number): void
  {
    const control = this.editForm.controls.filterFunctions;
    // If this id was already persisted, we must remove
    if (id > 0)
    {
      const rejectedId: any = {};
      rejectedId.id = id;
      this.reportEdit.filterFunctions = reject(this.reportEdit.filterFunctions, rejectedId);
    }
    control.removeAt(i);
    this.editForm.touched = true;
    this.editForm.markAsDirty();
  }

  private initFunction(id = 0, version = 0, name: string, functionCode: string, dataType: string,
    columnNameVar1: string, columnNameVar2: string, columnNameVar3: string): UntypedFormGroup
  {
    const group: any = {};
    group.id = [id];
    group.name = [{ value: name, disabled: id }, { validators: Validators.required }];
    group.functionCode = [functionCode, Validators.required];
    group.dataType = [dataType, Validators.required];
    group.columnNameVar1 = [columnNameVar1];
    group.columnNameVar2 = [columnNameVar2];
    group.columnNameVar3 = [columnNameVar3];
    group.version = version;
    return this.fb.group(group);
  }

  private setFilterFunctions(objectToPersist: any): void
  {
    objectToPersist.sqlViewFilter.filterFunctions.forEach(d =>
    {
      // Reset variables if not used
      if (!d.functionCode.toUpperCase().includes('$VAR1'))
      {
        d.columnNameVar1 = null;
      }
      if (!d.functionCode.toUpperCase().includes('$VAR2'))
      {
        d.columnNameVar2 = null;
      }
      if (!d.functionCode.toUpperCase().includes('$VAR3'))
      {
        d.columnNameVar3 = null;
      }
      // No need to post the following properties on the server
      delete d.sort;
    });
    delete objectToPersist.filterFunctions;
  }

  private initModel(reportEdit: ReportEdit)
  {
    if (reportEdit === undefined)
    {
      return;
    }

    // For Data Analyst, set private report by default
    if (this.isDataAnalyst && this.isNew)
    {
      reportEdit.privateReport = true;
      reportEdit.reportType = 'REPORT';
    }

    // Set form
    this.editForm = this.fb.group({
      id: [reportEdit.id],
      name: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
        },
      ],
      description: [
        { value: reportEdit.description, disabled: false }, Validators.required
      ],
      reportType: [
        { value: reportEdit.reportType, disabled: false }, Validators.required
      ],
      sqlViewName: [
        {
          value: reportEdit.sqlViewName,
          disabled: (reportEdit.reportType !== 'METRIC' && reportEdit.reportType !== 'REPORT')
        }, Validators.required
      ],
      privateReport: [
        { value: reportEdit.privateReport, disabled: reportEdit.reportType === 'METRIC' || this.isDataAnalyst }, Validators.required
      ],
      sqlViewFilter: [
        { value: reportEdit.sqlViewFilter, disabled: false }
      ],
      filterFunctions: this.fb.array([]),
    });

    this.editForm.get('reportType').valueChanges.subscribe({
      next: (reportType: string) =>
      {
        if (this.previousReportType !== null && reportType !== this.previousReportType)
        {
          this.clearViewSelection();
        }
        this.previousReportType = reportType;
        this.setPrivateState();
        this.setViewsAndState();
      }
    });

    if (reportEdit.sqlViewFilter === null)
    {
      reportEdit.sqlViewFilter = {};
      reportEdit.sqlViewFilter.state = null;
      reportEdit.sqlViewFilter.columns = null;
      reportEdit.sqlViewFilter.filterFunctions = null;
    }

    if (reportEdit && reportEdit.sqlViewFilter && reportEdit.sqlViewFilter.filterFunctions)
    {
      reportEdit.filterFunctions = sortBy(reportEdit.sqlViewFilter.filterFunctions, ['name']);
      reportEdit.sqlViewFilter.filterFunctions.forEach(data =>
      {
        this.addFunction(data.id, data.version, data.name, data.functionCode, data.dataType,
          data.columnNameVar1, data.columnNameVar2, data.columnNameVar3);
      });
    }

    // Keep track of grid settings changes
    this.gridSettings = {};
    this.gridSettings.state = JSON.parse(reportEdit.sqlViewFilter.state);
    this.gridSettings.columnsConfig = JSON.parse(reportEdit.sqlViewFilter.columns);

    // Keep the previous view name to control the reset of grid settings.
    this.previousViewName = reportEdit.sqlViewName;
    this.previousReportType = reportEdit.reportType;

    this.editForm.reset(reportEdit, false);
    this.staff = sortBy(reportEdit.users, ['fullName']);
    this.reportEdit = reportEdit;
    this.view.state.skip = 0;
    this.view.setData(this.staff);
    this.count = this.staff.length;

    this.editFormActive = true;
  }

  private closeEditForm(): void
  {
    this.selectedTab = 0;
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private clearViewSelection(): void
  {
    this.sqlViewName.setValue(null);
    this.sqlViewName.markAsTouched();
    this.previousViewName = null;
    this.gridSettings = '';
    if (this.selectedTab === 1)
    {
      this.selectedTab = 0;
    }
  }

  private setPrivateState(): void
  {
    if (this.reportType.value === 'METRIC' || this.isDataAnalyst)
    {
      this.privateReport.disable();
      this.privateReport.setValue(true);
    }
    else
    {
      this.privateReport.enable();
    }
  }

  private setViewsAndState(): void
  {

    if (this.reportType.value === 'METRIC' || this.reportType.value === 'REPORT')
    {
      this.sqlViewName.enable();
      this.viewService.getCachedViewNames(this.reportType.value).subscribe({
        next: (views) =>
        {          
          this.sqlViews = views;
          const extracts = [];
          this.sqlViews.forEach(data =>
          {            
            extracts.push({ text: data.name, value: data.viewName });
            this.extracts = sortBy(extracts, ['text']);
          });
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        },
        complete: () =>
        {
          this.view.stopLoading();
        }
      });
    }
    else
    {
      this.sqlViewName.disable();
      this.sqlViews = [];
    }
  }

  /**
   * Check if the function use some invalid methods for safety.
   *
   * @param functionCode Function code to validate.
   * @returns boolean Returns true if the function code contains black listed code.
   */
  private isBlackListedSQLKeywords(functionCode: string): boolean
  {
    const functionCodeUpper = functionCode.toLocaleUpperCase();
    return functionCodeUpper.includes('DELETE ') ||
      functionCodeUpper.includes('INSERT ') ||
      functionCodeUpper.includes('DROP ') ||
      functionCodeUpper.includes('UPDATE ') ||
      functionCodeUpper.includes('ALTER ') ||
      functionCodeUpper.includes('CREATE ') ||
      functionCodeUpper.includes('TRUNCATE ');
  }
}
